import React, { useState, useEffect, useCallback } from "react";
import { Loader2, Check, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import betaAPI from "v2/api";
import { formatDateTime } from "./constants";

const PAGE_SIZE = 30;

const humanize = (s = "") =>
  String(s)
    .replace(/_/g, " ")
    .replace(/^\w/, (c) => c.toUpperCase());

// Render a data value as a short chip label. Objects/arrays are stringified and
// every value is capped so a large blob (e.g. a full factory-test breakdown)
// can't explode the timeline; the full value is available on hover.
const CHIP_MAX = 48;
const chipValue = (v) => {
  const s = typeof v === "object" && v !== null ? JSON.stringify(v) : String(v);
  return s.length > CHIP_MAX ? `${s.slice(0, CHIP_MAX)}…` : s;
};
const chipTitle = (v) =>
  typeof v === "object" && v !== null ? JSON.stringify(v) : String(v);

const AttemptRow = ({ attempt, isLast }) => {
  const { t } = useTranslation();
  const passed = attempt.result === "pass";
  const dataEntries = attempt.data ? Object.entries(attempt.data) : [];

  return (
    <div className="relative flex gap-3 pb-5">
      {!isLast && (
        <span className="absolute left-[15px] top-8 h-full w-px bg-[#F3F4F6]" />
      )}
      <div
        className="z-10 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full"
        style={{ backgroundColor: passed ? "#DCFCE7" : "#FEE2E2" }}
      >
        {passed ? (
          <Check size={15} className="text-[#16A34A]" />
        ) : (
          <X size={15} className="text-[#DC2626]" />
        )}
      </div>

      <div className="min-w-0 flex-1 pt-1">
        <div className="flex items-center justify-between gap-2">
          <p className="text-sm font-medium text-[#111827]">
            {attempt.step_label || humanize(attempt.step)}
            <span
              className="ml-2 rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase"
              style={{
                color: passed ? "#16A34A" : "#DC2626",
                backgroundColor: passed ? "#F0FDF4" : "#FEF2F2",
              }}
            >
              {passed
                ? t("admin.manufacturing.attemptHistory.pass")
                : t("admin.manufacturing.attemptHistory.fail")}
            </span>
          </p>
          <span className="flex-shrink-0 text-[11px] text-[#9CA3AF]">
            {formatDateTime(attempt.created_at)}
          </span>
        </div>

        <p className="mt-0.5 text-xs text-[#9CA3AF]">
          {attempt.sub_stage_label || humanize(attempt.sub_stage)}
          {attempt.operator_name
            ? t("admin.manufacturing.attemptHistory.byOperator", {
                name: attempt.operator_name,
              })
            : ""}
        </p>

        {attempt.notes && (
          <p className="mt-1 text-[13px] text-[#374151]">{attempt.notes}</p>
        )}

        {dataEntries.length > 0 && (
          <div className="mt-1.5 flex flex-wrap gap-1.5">
            {dataEntries.map(([k, v]) => (
              <span
                key={k}
                title={`${k}: ${chipTitle(v)}`}
                className="max-w-[240px] truncate rounded bg-[#F3F4F6] px-1.5 py-0.5 font-mono text-[11px] text-[#374151]"
              >
                {k}: {chipValue(v)}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

/** Newest-first audit trail of every validation step attempt. */
const AttemptHistory = ({ bleMac }) => {
  const { t } = useTranslation();
  const [attempts, setAttempts] = useState([]);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);

  const loadPage = useCallback(
    async (pageNum) => {
      const res = await betaAPI.getWearableAttempts({
        bleMac,
        page: pageNum,
        pageSize: PAGE_SIZE,
      });
      const results = res.data?.results || [];
      const totalPages =
        res.data?.total_pages ??
        (results.length < PAGE_SIZE ? pageNum : pageNum + 1);
      setHasMore(pageNum < totalPages);
      return results;
    },
    [bleMac]
  );

  useEffect(() => {
    let active = true;
    setLoading(true);
    setPage(1);
    loadPage(1)
      .then((results) => active && setAttempts(results))
      .catch((err) => console.error("Failed to load attempts", err))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [loadPage]);

  const handleLoadMore = async () => {
    const nextPage = page + 1;
    setLoadingMore(true);
    try {
      const results = await loadPage(nextPage);
      setAttempts((prev) => [...prev, ...results]);
      setPage(nextPage);
    } catch (err) {
      console.error("Failed to load more attempts", err);
    } finally {
      setLoadingMore(false);
    }
  };

  return (
    <div className="rounded-xl border border-[#F3F4F6] bg-white">
      <div className="border-b border-[#F3F4F6] px-5 py-3.5">
        <h2 className="text-sm font-semibold text-[#111827]">
          {t("admin.manufacturing.attemptHistory.title")}
        </h2>
      </div>

      <div className="px-5 py-5">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 size={20} className="animate-spin text-[#E07A3A]" />
          </div>
        ) : attempts.length === 0 ? (
          <p className="py-6 text-center text-sm text-[#9CA3AF]">
            {t("admin.manufacturing.attemptHistory.empty")}
          </p>
        ) : (
          <>
            {attempts.map((attempt, index) => (
              <AttemptRow
                key={attempt.id ?? index}
                attempt={attempt}
                isLast={index === attempts.length - 1}
              />
            ))}

            {hasMore && (
              <button
                onClick={handleLoadMore}
                disabled={loadingMore}
                className="mt-1 flex w-full items-center justify-center gap-1.5 rounded-lg border border-[#E5E7EB] py-2 text-xs font-medium text-[#6B7280] transition duration-150 hover:bg-[#F9FAFB] disabled:opacity-60"
              >
                {loadingMore && <Loader2 size={13} className="animate-spin" />}
                {t("admin.manufacturing.attemptHistory.loadOlder")}
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AttemptHistory;
